import React, { useState } from "react";
import { Button } from "../../../../../ui-kit-v2/src/button/button";
import { _ } from "i18n";
import "./css/GeneratedReport.css";
import { GameAnalysisPhysicalResponse } from "../../../../../portal/src/libs/apiClient";
import Loader from "ui-kit/src/Loader/Loader";
import clsx from "clsx";
import { ChevronsRight, Download } from "lucide-react";
import { PhysicalAnalysisPdfViewer } from "./PhysicalAnalysisPdfViewer";

interface GeneratedReportProps {
	isLoading: boolean | undefined;
	physicalAnalysisReport?: GameAnalysisPhysicalResponse;
	error?: Error | null;
}

export function GeneratedReport({
	physicalAnalysisReport,
	isLoading,
	error,
}: GeneratedReportProps) {
	const [downloading, setDownloading] = useState<boolean>(false);
	const reportUrl = physicalAnalysisReport?.physicalMetricsReportUrl || null;
	const downloadableReportUrl =
		physicalAnalysisReport?.downloadableReportUrl || null;
	const canDownloadReport = downloadableReportUrl !== null;

	if (isLoading) {
		return <Loader />;
	}

	if (error) {
		return (
			<p className="error-text">
				{_("gamePage.physicalMetrics.errorLoadingData")}: {error.message}
			</p>
		);
	}

	if (!reportUrl) {
		return <p className="error-text">{_("info.nodata_available")}</p>;
	}

	return (
		<>
			<div className="physical-metrics__action-buttons">
				<Button
					onClick={async () => {
						if (!reportUrl) {
							return;
						}

						const link = document.createElement("a");
						link.href = reportUrl;
						link.target = "_blank";
						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);
					}}
					className={clsx(
						"btn btn--grey w-max physical-metrics__action-btn",
						{ disabled: downloading },
					)}
					iconLeft={<ChevronsRight size={16} strokeWidth={2} aria-hidden className="icon-view" />}
					text={_("permission.game_view")}
				/>
				<Button
					disabled={isLoading || downloading || !canDownloadReport}
					className={clsx(
						"btn btn--grey w-max physical-metrics__action-btn",
						{ disabled: downloading || !canDownloadReport },
					)}
					title={
						canDownloadReport
							? undefined
							: _("gamePage.physicalMetrics.downloadUnavailable")
					}
					onClick={async () => {
						if (!downloadableReportUrl) {
							return;
						}
						setDownloading(true);

						try {
							const link = document.createElement("a");
							link.href = downloadableReportUrl;
							document.body.appendChild(link);
							link.click();
							document.body.removeChild(link);
						} finally {
							setDownloading(false);
						}
					}}
					iconLeft={<Download size={16} strokeWidth={2} aria-hidden />}
					text={_("common.action.downloadPdf")}
				/>
			</div>

			<div className="physical-metrics-content">
				<PhysicalAnalysisPdfViewer url={reportUrl} />
			</div>
		</>
	);
}
